import { useState, useRef, useCallback } from "react";
import { trpc } from "../lib/trpc";
import { toast } from "sonner";
import { Camera, MapPin, CheckCircle, AlertCircle, Loader2, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Coordenadas dos locais permitidos (raio: 100 metros)
const LOCAIS_PERMITIDOS = [
  {
    nome: "R. Pref. Gabriel José Antônio, 184 — Guarulhos",
    lat: -23.4803359,
    lng: -46.5339362,
  },
  {
    nome: "Av. Carmela Dutra, 180 — Guarulhos",
    lat: -23.4364163,
    lng: -46.4328012,
  },
];
const RAIO_METROS = 350;

function calcularDistanciaMetros(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function validarLocalizacao(lat: number, lng: number): { valido: boolean; localNome?: string } {
  for (const local of LOCAIS_PERMITIDOS) {
    const dist = calcularDistanciaMetros(lat, lng, local.lat, local.lng);
    if (dist <= RAIO_METROS) {
      return { valido: true, localNome: local.nome };
    }
  }
  return { valido: false };
}

const TIPO_LABELS: Record<string, string> = {
  inicio_turno: "Início de Turno",
  fim_turno: "Fim de Turno",
  inicio_almoco: "Início do Horário de Almoço",
  fim_almoco: "Fim do Horário de Almoço",
};

type Etapa = "cpf" | "localizacao" | "foto" | "confirmacao" | "sucesso";

export default function RegistroPonto() {
  const [etapa, setEtapa] = useState<Etapa>("cpf");
  const [cpf, setCpf] = useState("");
  const [tipo, setTipo] = useState<string>("");
  const [localizacao, setLocalizacao] = useState<{ lat: number; lng: number; localNome: string } | null>(null);
  const [fotoBase64, setFotoBase64] = useState<string>("");
  const [fotoPreview, setFotoPreview] = useState<string>("");
  const [carregandoGeo, setCarregandoGeo] = useState(false);
  const [erroGeo, setErroGeo] = useState("");
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lng: number } | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [streamAtivo, setStreamAtivo] = useState(false);

  const registrarMut = trpc.ponto.registrar.useMutation({
    onSuccess: () => {
      setEtapa("sucesso");
    },
    onError: (err) => {
      toast.error(err.message || "Erro ao registrar ponto.");
    },
  });

  // ── Formatação de CPF ──────────────────────────────────────────────────────
  function formatarCpf(valor: string): string {
    const nums = valor.replace(/\D/g, "").slice(0, 11);
    if (nums.length <= 3) return nums;
    if (nums.length <= 6) return `${nums.slice(0, 3)}.${nums.slice(3)}`;
    if (nums.length <= 9) return `${nums.slice(0, 3)}.${nums.slice(3, 6)}.${nums.slice(6)}`;
    return `${nums.slice(0, 3)}.${nums.slice(3, 6)}.${nums.slice(6, 9)}-${nums.slice(9)}`;
  }

  // ── Etapa 1: CPF ──────────────────────────────────────────────────────────
  function avancarCpf() {
    const nums = cpf.replace(/\D/g, "");
    if (nums.length !== 11) {
      toast.error("Digite um CPF válido com 11 dígitos.");
      return;
    }
    if (!tipo) {
      toast.error("Selecione o tipo de registro.");
      return;
    }
    setEtapa("localizacao");
  }

  // ── Etapa 2: Localização ──────────────────────────────────────────────────
  function obterLocalizacao() {
    setCarregandoGeo(true);
    setErroGeo("");
    const timeoutId = setTimeout(() => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      setCarregandoGeo(false);
      setErroGeo("Não foi possível obter sua localização. Tente novamente.");
    }, 20000);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        if (accuracy > 150) return;
        clearTimeout(timeoutId);
        navigator.geolocation.clearWatch(watchIdRef.current!);
        watchIdRef.current = null;
        setGpsCoords({ lat: latitude, lng: longitude });
        const resultado = validarLocalizacao(latitude, longitude);
        setCarregandoGeo(false);
        if (resultado.valido && resultado.localNome) {
          setLocalizacao({ lat: latitude, lng: longitude, localNome: resultado.localNome });
          setEtapa("foto");
        } else {
          setErroGeo("Você não está em um local autorizado para registrar ponto.");
        }
      },
      (err) => {
        clearTimeout(timeoutId);
        setCarregandoGeo(false);
        setErroGeo(
          err.code === 1
            ? "Permissão de localização negada. Habilite nas configurações do navegador."
            : "Não foi possível obter sua localização. Tente novamente."
        );
      },
      { enableHighAccuracy: true, maximumAge: 0 }
    );
  }

  // ── Etapa 3: Foto ─────────────────────────────────────────────────────────
  const iniciarCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setStreamAtivo(true);
      }
    } catch {
      toast.error("Não foi possível acessar a câmera.");
    }
  }, []);

  function pararCamera() {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
      setStreamAtivo(false);
    }
  }

  function capturarFoto() {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setFotoBase64(dataUrl);
    setFotoPreview(dataUrl);
    pararCamera();
    setEtapa("confirmacao");
  }

  function retirarFoto() {
    setFotoBase64("");
    setFotoPreview("");
    setEtapa("foto");
    setTimeout(() => iniciarCamera(), 100);
  }

  // ── Etapa 4: Confirmação ──────────────────────────────────────────────────
  function confirmarRegistro() {
    if (!localizacao) return;
    registrarMut.mutate({
      cpf: cpf.replace(/\D/g, ""),
      tipo: tipo as any,
      fotoBase64,
      latitude: localizacao.lat,
      longitude: localizacao.lng,
    });
  }

  // ── Reiniciar ─────────────────────────────────────────────────────────────
  function reiniciar() {
    setCpf("");
    setTipo("");
    setLocalizacao(null);
    setFotoBase64("");
    setFotoPreview("");
    setErroGeo("");
    setStreamAtivo(false);
    setEtapa("cpf");
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start px-4 py-8">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 mb-4">
            <Camera className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Registro de Ponto</h1>
          <p className="text-sm text-gray-500 mt-1">Box Warehouse</p>
        </div>

        {/* Indicador de etapas */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {(["cpf", "localizacao", "foto", "confirmacao"] as Etapa[]).map((e, i) => (
            <div
              key={e}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                etapa === e
                  ? "bg-orange-500"
                  : ["cpf", "localizacao", "foto", "confirmacao"].indexOf(etapa) > i
                  ? "bg-orange-300"
                  : "bg-gray-200"
              }`}
            />
          ))}
        </div>

        {/* ── Etapa CPF ── */}
        {etapa === "cpf" && (
          <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Identificação</h2>
              <p className="text-sm text-gray-500">Digite seu CPF e selecione o tipo de registro.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cpf">CPF</Label>
              <Input
                id="cpf"
                type="tel"
                inputMode="numeric"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={(e) => setCpf(formatarCpf(e.target.value))}
                className="text-lg h-12"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Registro</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger className="h-12 text-base">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_LABELS).map(([val, label]) => (
                    <SelectItem key={val} value={val} className="text-base py-3">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={avancarCpf} className="w-full h-12 text-base bg-orange-500 hover:bg-orange-600">
              Continuar
            </Button>
          </div>
        )}

        {/* ── Etapa Localização ── */}
        {etapa === "localizacao" && (
          <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Verificação de Local</h2>
              <p className="text-sm text-gray-500">
                Você precisa estar em um dos locais autorizados para registrar o ponto.
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              {LOCAIS_PERMITIDOS.map((l) => (
                <div key={l.nome} className="flex items-start gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mt-0.5 text-orange-500 shrink-0" />
                  <span>{l.nome}</span>
                </div>
              ))}
            </div>
            {erroGeo && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <span>{erroGeo}</span>
                  {gpsCoords && (
                    <p className="mt-1 text-xs font-mono text-red-500">
                      GPS: {gpsCoords.lat.toFixed(7)}, {gpsCoords.lng.toFixed(7)}
                    </p>
                  )}
                </div>
              </div>
            )}
            <Button
              onClick={obterLocalizacao}
              disabled={carregandoGeo}
              className="w-full h-12 text-base bg-orange-500 hover:bg-orange-600"
            >
              {carregandoGeo ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verificando localização...
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4 mr-2" />
                  Verificar Minha Localização
                </>
              )}
            </Button>
            <Button variant="ghost" onClick={() => setEtapa("cpf")} className="w-full h-10 text-sm">
              Voltar
            </Button>
          </div>
        )}

        {/* ── Etapa Foto ── */}
        {etapa === "foto" && (
          <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Foto do Rosto</h2>
              <p className="text-sm text-gray-500">Posicione seu rosto no centro e tire a foto.</p>
            </div>
            <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
              <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
              {!streamAtivo && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Button
                    onClick={iniciarCamera}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Abrir Câmera
                  </Button>
                </div>
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />
            {streamAtivo && (
              <Button onClick={capturarFoto} className="w-full h-12 text-base bg-orange-500 hover:bg-orange-600">
                <Camera className="w-4 h-4 mr-2" />
                Capturar Foto
              </Button>
            )}
          </div>
        )}

        {/* ── Etapa Confirmação ── */}
        {etapa === "confirmacao" && (
          <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Confirmar Registro</h2>
              <p className="text-sm text-gray-500">Verifique os dados antes de confirmar.</p>
            </div>
            {fotoPreview && (
              <div className="relative rounded-xl overflow-hidden aspect-[4/3]">
                <img src={fotoPreview} alt="Foto capturada" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">CPF</span>
                <span className="font-medium">{cpf}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Tipo</span>
                <span className="font-medium">{TIPO_LABELS[tipo]}</span>
              </div>
              {localizacao && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Local</span>
                  <span className="font-medium text-right max-w-[60%]">{localizacao.localNome}</span>
                </div>
              )}
            </div>
            <Button
              onClick={confirmarRegistro}
              disabled={registrarMut.isPending}
              className="w-full h-12 text-base bg-orange-500 hover:bg-orange-600"
            >
              {registrarMut.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registrando...
                </>
              ) : (
                "Confirmar Registro"
              )}
            </Button>
            <Button variant="ghost" onClick={retirarFoto} className="w-full h-10 text-sm">
              <RotateCcw className="w-4 h-4 mr-2" />
              Tirar Foto Novamente
            </Button>
          </div>
        )}

        {/* ── Etapa Sucesso ── */}
        {etapa === "sucesso" && (
          <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-5 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Ponto Registrado!</h2>
              {registrarMut.data && (
                <p className="text-sm text-gray-500 mt-2">
                  {registrarMut.data.nome} — Matrícula {registrarMut.data.matricula}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-1">{TIPO_LABELS[tipo]}</p>
            </div>
            <Button onClick={reiniciar} variant="outline" className="w-full h-12 text-base">
              Novo Registro
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
